import classnames from 'classnames/bind'
import { useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { useTranslate } from 'react-polyglot'
import { IconUspSliderItem } from '~/@types/cart'
import { GlobalThemeColors } from '~/@types/colors'
import { GlobalGridPreset } from '~/@types/grid-preset'
import { GlobalTextPreset } from '~/@types/text-preset'
import { GlobalTextStyling } from '~/@types/text-styling'

import { UnlikelyMoney } from '@unlikelystudio/commerce-connector'
import { useMeasureObserver, useWindowSize } from '@unlikelystudio/react-hooks'

import RichText, { RichTextBlock } from '~/components/Abstracts/RichText'
import Tag from '~/components/Abstracts/Tag'
import AddToCart from '~/components/AddToCart'
import { CartPopInHeader } from '~/components/Cart/CartPopInHeader'
import { useNavBannerContext } from '~/components/Navigation/Navigation/NavBanner'
import { AlertStockPopinProps, PopInStockAlert } from '~/components/PopInAlerts'
import AccordionItems, {
  AccordionItemProps,
} from '~/components/UI/AccordionItems'
import BlurredCta from '~/components/UI/BlurredCta'
import Breadcrumb from '~/components/UI/Breadcrumb'
import InlineCta from '~/components/UI/InlineCta'
import DeliveryInformations from '~/components/UI/ProductHeader/DeliveryInformations'
import { MultiBundlePreview } from '~/components/UI/ProductHeader/MultiBundlePreview'
import ProductHeaderMedia, {
  ProductHeaderMediaProps,
} from '~/components/UI/ProductHeaderMedia'
import ProductInfos from '~/components/UI/ProductInfos'
import { ProductReviewsListProps } from '~/components/UI/ProductReviewsList'
import { ProductUpsales } from '~/components/UI/ProductUpsales'
import { ByCharlotVariantSelector } from '~/components/UI/ProductVariantSelector/ByCharlotVariantSelector'
import { PackProductSelector } from '~/components/UI/ProductVariantSelector/PackProductSelector'
import ProductNote from '~/components/UI/ProductVariantSelector/ProductNote'
import StarRatings from '~/components/UI/StarRatings'
import { UspSlider } from '~/components/UI/UspSlider'

import { usePopIn } from '~/providers/PopInProvider'
import { useProductHeaderProvider } from '~/providers/ProductPageProvider'
import { useShopifyProductContext } from '~/providers/ShopifyProductProvider'
import { useStyle } from '~/providers/StyleProvider'
import { useUserSelectionProvider } from '~/providers/UserSelectionProvider'

import useBodyScrollLock from '~/hooks/useBodyScrollLock'
import { useGetStateInformationsFromUserSelection } from '~/hooks/useGetStateInformationsFromUserSelection'

import numberAsBoolean from '~/utils/number-as-boolean'

import { CUSTOMIZER, PRODUCT } from '~/data/dictionary'
import { SerializedProduct } from '~/data/product-card-data'

import ProductVariantSelector from '../ProductVariantSelector'
import css from './styles.module.scss'

const cx = classnames.bind(css)

export interface AkairosPostPreviewReturn {
  allowImage?: boolean
  computeSubTitleFirst: boolean
  count: number
  formattedSubTitle: string
  formattedTitle: string
  html: string
  key: string
  labelSize: string
  labelSku: string
  maxSubTitleLines: number
  maxTitleLines: number
  rawSubTitle: string
  rawTitle: string
  subTitleLinesCount: number
  titleLinesCount: number
  titleSizeClass: string
  topMarginClass: string
  underSubTitleMarginClass: string
  underTitleMarginClass: string
}

export interface ProductHeaderProps
  extends ProductDetailsProps,
    Pick<SerializedProduct, 'media'> {
  deliveryTime?: string
  className?: string
  categories?: string[]
  meanings?: string[]
  stockAlertEnabled?: boolean
  stockAlertProps?: AlertStockPopinProps
  isGiftCard?: boolean
  hasCompleteWith?: boolean
  excludeMetalPot?: boolean
  excludeCeramicPot?: boolean
  featuredImage?: string
  accordions?: AccordionItemProps[]
  reinsuranceSlider?: IconUspSliderItem[]
  productBigParagraph?: RichTextBlock
  byCharlotCustomizable?: boolean
  hideSweetNote?: boolean
  showImageVariants?: boolean
  ratings?: number
  reviews?: Omit<ProductReviewsListProps, 'className' | 'innerRef'>
  productReviewsListRef?: any
  shopifyProduct?: SerializedProduct
  pack?: SerializedProduct[][]
  keepMediaRatio?: boolean
  isMultiBundle?: boolean
  isSimpleProductPreOrder?: boolean
}

export interface ProductDetailsProps extends Omit<SerializedProduct, 'media'> {
  className?: string
  price?: UnlikelyMoney
  productSlug?: string
}

export interface MobileVariantSelectorProps {
  className?: string
  isGiftCard?: boolean
  showImageVariants?: boolean
  close?(): void
  items?: ProductHeaderMediaProps['items']
}

export function MobileVariantSelector({
  isGiftCard,
  showImageVariants,
  className,
  close,
  items,
}: MobileVariantSelectorProps) {
  const t = useTranslate()
  const gridStyle = useStyle({
    grid: GlobalGridPreset.BASE_GRID,
  })
  const { setUserSelection } = useUserSelectionProvider()
  const { activeTabIndex, product, activeTabAvailableProducts } =
    useGetStateInformationsFromUserSelection()

  return (
    <div className={cx(css.MobileVariantSelector, className)}>
      <CartPopInHeader
        className={cx(css.header)}
        title={t(CUSTOMIZER.EDIT_PRODUCT(product?.productType))}
        close={close}
        alignTitleLeft
      />
      <div className={cx(css.container, gridStyle)}>
        <div className={cx(css.content)}>
          <ProductHeaderMedia className={cx(css.media)} items={items} inPopIn />
          {activeTabAvailableProducts?.length > 1 && (
            <PackProductSelector className={cx(css.packProductSelector)} />
          )}

          <ByCharlotVariantSelector
            className={cx(css.variantSelector)}
            isGiftCard={isGiftCard}
            showImageVariants={showImageVariants}
          />
          <BlurredCta
            isBig
            className={cx(css.validateCta)}
            theme={GlobalThemeColors.Black}
            onClick={() => close?.()}>
            {t(CUSTOMIZER.VALIDATE)}
          </BlurredCta>
          <div className={cx(css.nextProductContainer)}>
            <InlineCta
              className={cx(css.nextProductCta)}
              textPreset={GlobalTextPreset.Cta10Primary}
              theme={GlobalThemeColors.Gray1ToBlack}
              lineProps={{
                initialLineState: 'underlined',
              }}
              onClick={() => {
                setUserSelection((prev) => {
                  const nextIndex =
                    activeTabIndex + 1 < prev?.length ? activeTabIndex + 1 : 0
                  const nextState = prev.map((tab, index) => ({
                    ...tab,
                    isActive: index === nextIndex,
                  }))
                  return nextState
                })
              }}>
              {t(CUSTOMIZER.NEXT_PRODUCT)}
            </InlineCta>
          </div>
        </div>
      </div>
    </div>
  )
}

function ProductHeader({
  className,
  media,
  productName,
  deliveryTime,
  availableForSale,
  smallDescription,
  bigDescription,
  stockAlertEnabled,
  stockAlertProps,
  isGiftCard,
  accordions,
  hasUpsales,
  hasCompleteWith,
  showImageVariants,
  productBigParagraph,
  reinsuranceSlider,
  hideSweetNote,
  ratings,
  reviews,
  keepMediaRatio,
  productReviewsListRef,
  isMultiBundle,
  isSimpleProductPreOrder,
}: ProductHeaderProps) {
  const {
    addToCartDefaultProps,
    shopifyProduct,
    isLoadingLabelPreviewData,
    customizationImageState: [customizationImages],
  } = useProductHeaderProvider()
  const { setUserSelection } = useUserSelectionProvider()
  const t = useTranslate()
  const gridStyle = useStyle({
    grid: GlobalGridPreset.BASE_GRID,
  })

  const borderStyle = useStyle({
    borderColor: GlobalThemeColors.Gray3ToBlack,
  })

  const { isLoading } = useShopifyProductContext()

  const titleTextSyle = useStyle({
    textPreset: GlobalTextPreset.Title12Primary,
    textStyling: GlobalTextStyling.UpperCase,
  })

  const productInfosContainerRef = useRef<HTMLDivElement>()
  const [withBanner] = useNavBannerContext()
  const hasMultipleVariants = shopifyProduct?.variants?.length > 1
  const {
    variants,
    currentVariant,
    itemsToSelect,
    activeTab,
    product,
    isBundle,
    customizerVariants,
  } = useGetStateInformationsFromUserSelection()

  const [isZooming, setIsZooming] = useState(false)

  const handleZoom = (state) => {
    setIsZooming(state)
  }

  const gridRef = useRef()
  const { width } = useMeasureObserver(gridRef)

  const showCartInfos =
    isMultiBundle || isBundle || variants?.length > 1 || itemsToSelect > 1

  const { entry, inView, ref } = useInView({})
  const multiBundleProduct = shopifyProduct
  const defaultPrice = isMultiBundle
    ? multiBundleProduct?.defaultVariant?.price ?? null
    : product?.defaultVariant?.price ?? null
  const compareAtPrice = isMultiBundle
    ? multiBundleProduct?.defaultVariant?.compareAtPrice
    : currentVariant?.compareAtPrice ?? null
  const shouldOpenStockAlert = isMultiBundle
    ? !multiBundleProduct?.availableForSale && stockAlertEnabled
    : !currentVariant?.availableForSale && stockAlertEnabled

  const mediaGallery = [
    ...(product?.byCharlotCustomizable &&
    customizerVariants?.[0] &&
    activeTab &&
    customizerVariants?.[0].variant?.variantImage?.src
      ? [
          {
            image: {
              ...customizerVariants?.[0].variant?.variantImage,
              alt: customizerVariants?.[0].variant?.title,
            },
          },
        ]
      : []),
    ...(product?.byCharlotCustomizable &&
    customizerVariants?.length == 0 &&
    activeTab &&
    product?.defaultVariant?.image?.src
      ? [
          {
            image: {
              ...product?.defaultVariant?.image,
              alt: product?.title,
            },
          },
        ]
      : []),
    ...(!product?.byCharlotCustomizable &&
    customizerVariants?.[0]?.variant?.image?.src &&
    activeTab
      ? [
          {
            image: {
              ...customizerVariants?.[0]?.variant?.image,
              alt: customizerVariants?.[0].variant?.title,
            },
          },
        ]
      : []),
    ...(!product?.byCharlotCustomizable &&
    customizerVariants.length < 1 &&
    currentVariant?.image?.src &&
    activeTab
      ? [
          {
            image: { ...currentVariant?.image, alt: currentVariant?.title },
          },
        ]
      : []),
    ...(isMultiBundle
      ? [
          {
            image: {
              ...shopifyProduct?.defaultVariant?.image,
              alt: shopifyProduct?.defaultVariant?.title,
            },
          },
        ]
      : []),
    ...media,
  ]

  const withBottomInfos = !!deliveryTime

  const BottomInfos = () => {
    return (
      withBottomInfos && (
        <div className={css.bottomInfos}>
          <div className={css.delivery}>
            <span className={cx(css.deliveryLabel, titleTextSyle)}>
              {t(PRODUCT.DELIVERY)}
            </span>
            <Tag
              className={css.deliveryTime}
              label={deliveryTime}
              textPreset={GlobalTextPreset.Label10Secondary}
              theme={GlobalThemeColors.Shade1}
            />
          </div>
        </div>
      )
    )
  }

  const bigParagraphStyle = useStyle({
    textPreset: GlobalTextPreset.Title10Primary,
    textStyling: GlobalTextStyling.UpperCase,
  })

  const [isMultiBundlePreviewOpen, setMultiBundlePreviewOpen] = useState(false)

  const { add } = usePopIn()

  const showStockAlertPopin = () =>
    add(
      <PopInStockAlert
        currentVariant={
          isMultiBundle ? shopifyProduct.defaultVariant : currentVariant
        }
        stockAlertProps={stockAlertProps}
      />,
    )

  const handleMultiBundlePreview = (state) => {
    setMultiBundlePreviewOpen(state)
  }

  // used to display our fake popin bundle variant selector for mobile bycharlot
  const [isByCharlotMobileOpen, setByCharlotMobileOpen] = useState(false)
  useBodyScrollLock(isByCharlotMobileOpen, null, 'mobile-variant-selector')

  const productDetailRef = useRef()
  const productDetailBounds = useMeasureObserver(
    productDetailRef,
    'getBoundingClientRect',
  )

  const windowSize = useWindowSize()

  // Sticky on both direction product details
  const style = {
    top:
      windowSize.height < productDetailBounds.height
        ? windowSize.height - productDetailBounds.height
        : 0,
  }

  return (
    <div
      className={cx(css.ProductHeader, className, borderStyle, {
        withBanner,
        isZooming,
      })}>
      {isByCharlotMobileOpen && (
        <MobileVariantSelector
          className={cx(css.MobileVariantSelector)}
          items={mediaGallery}
          close={() => {
            setByCharlotMobileOpen(false)
            setUserSelection((prev) =>
              prev.map((tab) => ({ ...tab, isActive: false })),
            )
          }}
          isGiftCard={isGiftCard}
          showImageVariants={showImageVariants}
        />
      )}
      {isMultiBundlePreviewOpen && (
        <MultiBundlePreview
          className={cx(css.preview)}
          close={() => setMultiBundlePreviewOpen(false)}
        />
      )}

      <div className={cx(css.productHeaderContainer, gridStyle)}>
        <div
          className={cx(css.images)}
          style={keepMediaRatio ? style : {}}
          ref={productDetailRef}>
          <ProductHeaderMedia
            className={css.productHeaderMedia}
            openPreview={handleMultiBundlePreview}
            items={mediaGallery}
            handleZoom={handleZoom}
            keepMediaRatio={keepMediaRatio}
          />
        </div>

        <div className={cx(css.grid, css.productHeaderInfos)}>
          <div ref={gridRef}>
            <div
              ref={productInfosContainerRef}
              className={cx(css.productInfosContainer)}>
              <Breadcrumb className={cx(css.breadcrumb)} />
              <ProductInfos
                hasMultipleVariants={hasMultipleVariants}
                price={defaultPrice}
                compareAtPrice={compareAtPrice}
                smallDescription={smallDescription}
                bigDescription={bigDescription}
                availableForSale={availableForSale}
                productName={productName}
                isGiftCard={isGiftCard}
              />
              {numberAsBoolean(ratings) && reviews && (
                <StarRatings
                  className={cx(css.starsRatings)}
                  ratings={ratings}
                  reviews={reviews}
                  productReviewsListRef={productReviewsListRef}
                  isClickable
                />
              )}
              {showCartInfos && (
                <ProductVariantSelector
                  showImageVariants={showImageVariants}
                  isGiftCard={isGiftCard}
                  setByCharlotMobileOpen={setByCharlotMobileOpen}
                />
              )}

              {!hideSweetNote && (
                <ProductNote className={cx(css.productNote)} />
              )}

              {hasCompleteWith && (
                <ProductUpsales className={cx(css.productNote)} />
              )}

              <div className={css.addToCartContainer}>
                <span className={css.morphAddToCart} ref={ref} />
                {!shouldOpenStockAlert && (
                  <AddToCart
                    {...addToCartDefaultProps}
                    isSimpleProductPreOrder={isSimpleProductPreOrder}
                    withStockAlert={shouldOpenStockAlert}
                    customizationImages={customizationImages}
                    className={cx(css.addToCart, {
                      [css.withImage]: variants?.length > 1,
                    })}
                    loading={isLoading || isLoadingLabelPreviewData}
                  />
                )}
                {shouldOpenStockAlert && (
                  <AddToCart
                    {...addToCartDefaultProps}
                    isSimpleProductPreOrder={isSimpleProductPreOrder}
                    withStockAlert={shouldOpenStockAlert}
                    className={cx(css.addToCart, {
                      [css.withImage]: variants?.length > 1,
                    })}
                    onClick={() => {
                      showStockAlertPopin()
                    }}
                    loading={isLoading || isLoadingLabelPreviewData}
                  />
                )}
              </div>
              <div className={cx(css.hideOnSmallScreen)}>
                <DeliveryInformations
                  className={cx(css.deliveryInformations)}
                  productSku={product?.defaultVariant?.sku}
                />

                <RichText
                  className={cx(css.bigParagraph, bigParagraphStyle)}
                  render={productBigParagraph}
                />
                {accordions?.length > 0 && (
                  <AccordionItems
                    className={cx(css.accordions)}
                    items={accordions}
                    color={GlobalThemeColors.Gray1ToBlack}
                    layout={'productHeader'}
                    headlineTextPreset={GlobalTextPreset.Title10Primary}
                    bodyTextPreset={GlobalTextPreset.Text12Secondary}
                  />
                )}
                {reinsuranceSlider?.length > 0 && (
                  <UspSlider
                    className={cx(css.reinsuranceSlider)}
                    items={reinsuranceSlider}
                    layout="product-page"
                  />
                )}
                <BottomInfos />
              </div>
            </div>
          </div>
        </div>
        <div
          className={cx(css.stickyAddToCartZone, {
            isVisible: !inView && entry?.boundingClientRect?.top > 0,
          })}>
          {showCartInfos && (
            <div className={cx(css.stickyAddToCartContainer)}>
              <div className={css.stickyAddToCartButtonContainer}>
                {!shouldOpenStockAlert && (
                  <AddToCart
                    {...addToCartDefaultProps}
                    isSimpleProductPreOrder={isSimpleProductPreOrder}
                    withStockAlert={shouldOpenStockAlert}
                    customizationImages={customizationImages}
                    onClick={() => {
                      if (addToCartDefaultProps.disabled) {
                        const scrollY =
                          productInfosContainerRef.current?.offsetTop
                        if (scrollY) {
                          window.scrollTo(0, scrollY)
                        }
                      }
                    }}
                    className={cx(css.addToCart, css.stickyAddTocart)}
                    loading={isLoading || isLoadingLabelPreviewData}
                    isFullscreen={true}
                  />
                )}
                {shouldOpenStockAlert && (
                  <AddToCart
                    {...addToCartDefaultProps}
                    isSimpleProductPreOrder={isSimpleProductPreOrder}
                    withStockAlert={shouldOpenStockAlert}
                    theme={GlobalThemeColors.Black}
                    className={cx(css.addToCart, css.stickyAddTocart)}
                    onClick={() => {
                      showStockAlertPopin()
                    }}
                    style={{ width: inView ? width : undefined }}
                    loading={isLoading || isLoadingLabelPreviewData}
                    isFullscreen={!inView}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className={cx(css.hideOnLargeScreen, gridStyle)}>
        <DeliveryInformations className={cx(css.deliveryInformations)} />
        <RichText
          className={cx(css.bigParagraph, bigParagraphStyle)}
          render={productBigParagraph}
        />
        {accordions?.length > 0 && (
          <AccordionItems
            className={cx(css.accordions)}
            items={accordions}
            color={GlobalThemeColors.Gray1ToBlack}
            layout={'productHeader'}
            headlineTextPreset={GlobalTextPreset.Title10Primary}
            bodyTextPreset={GlobalTextPreset.Text12Secondary}
          />
        )}
        {reinsuranceSlider?.length > 0 && (
          <UspSlider
            className={cx(css.reinsuranceSlider)}
            items={reinsuranceSlider}
            layout="product-page"
          />
        )}

        <BottomInfos />
      </div>
    </div>
  )
}

ProductHeader.defaultProps = {}

export default ProductHeader
